import { BUSINESS_TIME_ZONE, computeDueAt, cutoffFor, type LocalDay } from '@noconflict/contracts';
import { createFormatter } from '../../../shared/i18n/format';

/** What the form says about one turnaround: when to order by, and when the report would arrive. */
export type DeliveryEstimate = {
  turnaroundDays: number;
  /** The next cut-off: noon in Toronto on a business day. */
  cutoffAt: Date;
  /** When a report ordered by the cut-off is due. */
  dueAt: Date;
  /** The cut-off as Toronto reads it, e.g. "12:00 PM". */
  cutoffTime: string;
  /** The same moment on the viewer's clock, or null when it reads the same (the viewer is on Toronto time). */
  localCutoffTime: string | null;
  /** Whether the cut-off is still today for the viewer; otherwise `cutoffDay` names the day. */
  cutoffIsToday: boolean;
  cutoffDay: string;
  /** The due date and time on the viewer's clock. */
  deliveredBy: string;
};

/**
 * The delivery estimate of each turnaround, from the same function the worker uses when the order is placed
 * (`computeDueAt`), counted from the next cut-off. `timeZone` is the viewer's; tests pin it.
 */
export function deliveryEstimates({
  now,
  holidays,
  turnarounds,
  locale,
  timeZone,
}: {
  now: Date;
  holidays: readonly LocalDay[];
  turnarounds: readonly number[];
  locale: string;
  timeZone?: string;
}): DeliveryEstimate[] {
  const viewer = createFormatter(locale, timeZone ? { timeZone } : {});
  const toronto = createFormatter(locale, { timeZone: BUSINESS_TIME_ZONE });
  const cutoffAt = cutoffFor(now, holidays);
  const cutoffTime = toronto.formatTime(cutoffAt);
  const localTime = viewer.formatTime(cutoffAt);
  return turnarounds.map((turnaroundDays) => {
    const dueAt = computeDueAt({ placedAt: cutoffAt, turnaroundDays, holidays });
    return {
      turnaroundDays,
      cutoffAt,
      dueAt,
      cutoffTime,
      localCutoffTime: localTime === cutoffTime ? null : localTime,
      cutoffIsToday: viewer.formatDate(cutoffAt) === viewer.formatDate(now),
      cutoffDay: viewer.formatWeekday(cutoffAt),
      deliveredBy: viewer.formatDateTime(dueAt),
    };
  });
}
